import { useCallback, useMemo } from 'react';

import { useParam } from './useParam';

type UseSortReturn<T = string, NodeType = {}> = {
  sort: SortState<T>;
  setSort: (name: T) => void;
  sorted: NodeType[];
};

const getNextDirection = (currentDirection: SortDirection): SortDirection => {
  if (currentDirection === 'asc') return 'desc';

  return currentDirection === 'desc' ? undefined : 'asc';
};

const createComparator = (sort: SortState<unknown>) => {
  const { name, direction } = sort;

  const nullOrder = direction === 'asc' ? 1 : -1;

  const compare = <NodeType = {}>(a: NodeType, b: NodeType) => {
    const va = a[name as keyof NodeType];
    const vb = b[name as keyof NodeType];

    const isANull = va == null;
    const isBNull = vb == null;

    if (isANull && !isBNull) return nullOrder;
    if (!isANull && isBNull) return -nullOrder;
    if (isANull && isBNull) return 0;

    if (typeof va === 'string' && typeof vb === 'string') {
      const result = va.trim().localeCompare(vb.trim());
      return direction === 'asc' ? result : -result;
    }

    if (va < vb) return direction === 'asc' ? -1 : 1;
    if (va > vb) return direction === 'asc' ? 1 : -1;
    return 0;
  };

  return compare;
};

type UseSortParams<T = string, NodeType = {}> = {
  useUrl?: boolean;
  defaultValue?: SortState<T>;
  nodes?: NodeType[];

  storageKey?: string;
  storageType?: 'session' | 'local';
};

export const useSort = <T = string, NodeType = {}>({
  useUrl,
  defaultValue,
  nodes,
  storageKey,
  storageType,
}: UseSortParams<T, NodeType> = {}): UseSortReturn<T, NodeType> => {
  const [sort, setSortState] = useParam<SortState<T>>({
    name: 'sort',
    enableUrl: useUrl ?? true,
    defaultValue: defaultValue ?? { name: undefined, direction: undefined },

    storageKey,
    storageType,
  });

  const setSort = useCallback(
    (name: T) => {
      if (sort?.name !== name) {
        return setSortState({ name, direction: 'asc' });
      }

      const direction = getNextDirection(sort.direction);
      const nextState = direction ? { name, direction } : undefined;
      return setSortState(nextState);
    },
    [sort, setSortState]
  );

  const sorted = useMemo(() => {
    if (!nodes?.length || !sort?.name || !sort.direction) {
      return nodes || [];
    }

    return [...nodes].sort(createComparator(sort));
  }, [nodes, sort]);

  return { sort: sort as SortState<T>, setSort, sorted };
};
